import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../utils/api';
import { Card, Btn, Input, Select, Modal, PageHeader, Badge, fmtDate } from '../components/UI';
import RocketLoader from '../components/RocketLoader';

const CATEGORIES = ['Career Advice', 'Resume Tips', 'Job Search', 'Interview Prep', 'Industry Insights', 'Success Stories'];

const emptyPost = () => ({
  title: '', excerpt: '', content: '', coverImage: '', category: 'Career Advice',
  tags: '', author: 'MarsResume Team', authorAvatar: '', readTime: 5,
  published: false, featured: false,
});

const Textarea = ({ label, value, onChange, rows = 4, placeholder = '', mono = false }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>{label}</label>}
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
      style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: mono ? 12 : 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: mono ? 'monospace' : 'inherit' }} />
  </div>
);

const PostEditor = ({ initial, onSave, onClose }) => {
  const [form,   setForm]   = useState({ ...emptyPost(), ...initial, tags: Array.isArray(initial?.tags) ? initial.tags.join(', ') : (initial?.tags || '') });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const isNew = !initial?._id;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleImage = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => set('coverImage', reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const submit = async () => {
    setError('');
    if (!form.title.trim()) return setError('Title is required');
    if (!form.content.trim()) return setError('Content is required');
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean), readTime: Number(form.readTime) || 5 };
      await onSave(payload);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'New Blog Post' : 'Edit Blog Post'} onClose={onClose} width={720}>
      {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}

      <Input label="Title *" value={form.title} onChange={v => set('title', v)} placeholder="e.g. 10 Resume Mistakes to Avoid" />
      <Textarea label="Excerpt (shown in listing)" value={form.excerpt} onChange={v => set('excerpt', v)} rows={2} placeholder="Short summary of the post…" />
      <Textarea label="Content (HTML) *" value={form.content} onChange={v => set('content', v)} rows={14} mono
        placeholder="<h2>Introduction</h2><p>Your content here...</p>" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Select label="Category" value={form.category} onChange={v => set('category', v)}
          options={CATEGORIES.map(c => [c, c])} />
        <Input label="Read Time (minutes)" value={String(form.readTime)} onChange={v => set('readTime', v)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Author Name" value={form.author} onChange={v => set('author', v)} />
        <Input label="Author Avatar URL" value={form.authorAvatar} onChange={v => set('authorAvatar', v)} placeholder="https://..." />
      </div>

      <Input label="Tags (comma-separated)" value={form.tags} onChange={v => set('tags', v)} placeholder="resume, career, tips" />

      {/* Cover image */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Cover Image</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {form.coverImage && (
            <img src={form.coverImage} alt="cover" style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 6, border: '1px solid #E2E8F0', flexShrink: 0 }} />
          )}
          <label style={{ cursor: 'pointer', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '8px 14px', fontSize: 12, fontWeight: 600, color: '#64748B' }}>
            📷 Upload Image (max 2 MB)
            <input type="file" accept="image/*" onChange={handleImage} style={{ display: 'none' }} />
          </label>
          {form.coverImage && <button onClick={() => set('coverImage', '')} style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Remove</button>}
        </div>
        <Input label="Or paste image URL" value={form.coverImage?.startsWith('data:') ? '' : form.coverImage} onChange={v => set('coverImage', v)} placeholder="https://..." />
      </div>

      {/* Flags */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 20 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#334155', fontWeight: 600 }}>
          <input type="checkbox" checked={form.published} onChange={e => set('published', e.target.checked)} style={{ width: 15, height: 15, accentColor: '#059669' }} />
          Published (visible on site)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#334155', fontWeight: 600 }}>
          <input type="checkbox" checked={form.featured} onChange={e => set('featured', e.target.checked)} style={{ width: 15, height: 15, accentColor: '#D97706' }} />
          ⭐ Featured post
        </label>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn color="ghost" onClick={onClose}>Cancel</Btn>
        <Btn color="blue" onClick={submit} disabled={saving}>{saving ? 'Saving…' : isNew ? 'Create Post' : 'Save Changes'}</Btn>
      </div>
    </Modal>
  );
};

const DeleteModal = ({ post, onConfirm, onClose }) => {
  const [loading, setLoading] = useState(false);
  return (
    <Modal title="Delete Post" onClose={onClose} width={400}>
      <p style={{ color: '#64748B', fontSize: 14, marginBottom: 20 }}>
        Are you sure you want to delete <strong style={{ color: '#0F172A' }}>"{post.title}"</strong>? This cannot be undone.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn color="ghost" onClick={onClose}>Cancel</Btn>
        <Btn color="red" onClick={async () => { setLoading(true); await onConfirm(); }} disabled={loading}>
          {loading ? 'Deleting…' : 'Delete'}
        </Btn>
      </div>
    </Modal>
  );
};

export const BlogPage = () => {
  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor,  setEditor]  = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [search,  setSearch]  = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getAllBlogPosts()
      .then(setPosts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    if (editor._id) await adminApi.updateBlogPost(editor._id, data);
    else            await adminApi.createBlogPost(data);
    load();
  };

  const handleDelete = async () => {
    await adminApi.deleteBlogPost(confirm._id);
    setConfirm(null);
    load();
  };

  const togglePublish = async (post) => {
    await adminApi.updateBlogPost(post._id, { published: !post.published });
    load();
  };

  const filtered = posts.filter(p =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        title="Blog Posts"
        subtitle={`${posts.length} total · ${posts.filter(p => p.published).length} published · ${posts.filter(p => p.featured).length} featured`}
        action={<Btn color="blue" onClick={() => setEditor({})}>+ New Post</Btn>}
      />

      {/* Search bar */}
      <div style={{ marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search posts by title or category…"
          style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, padding: '9px 14px', fontSize: 13, width: 320, outline: 'none', color: '#0F172A' }} />
      </div>

      <Card style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
            <RocketLoader size={140} message="Loading posts…" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748B', fontSize: 13 }}>
            {posts.length === 0 ? 'No posts yet. Create your first blog post!' : 'No posts match your search.'}
          </div>
        ) : (
          filtered.map((post, i) => (
            <div key={post._id} style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
              borderBottom: i < filtered.length - 1 ? '1px solid #E2E8F0' : 'none',
            }}>
              {/* Cover thumbnail */}
              {post.coverImage ? (
                <img src={post.coverImage} alt="" style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid #E2E8F0' }} />
              ) : (
                <div style={{ width: 60, height: 40, background: '#F1F5F9', borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📝</div>
              )}

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, color: '#0F172A', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{post.title}</span>
                  {post.featured && <Badge color="yellow">⭐ Featured</Badge>}
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#94A3B8', flexWrap: 'wrap' }}>
                  <span>{post.category}</span>
                  <span>{post.readTime} min read</span>
                  <span>{post.views || 0} views</span>
                  {post.publishedAt && <span>{fmtDate(post.publishedAt)}</span>}
                  {post.tags?.length > 0 && <span>{post.tags.slice(0, 3).join(', ')}</span>}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <button onClick={() => togglePublish(post)} style={{
                  background: post.published ? '#DCFCE7' : '#F1F5F9', color: post.published ? '#15803D' : '#64748B',
                  border: 'none', borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                }}>{post.published ? 'Published' : 'Draft'}</button>
                <Btn color="ghost" size="sm" onClick={() => setEditor(post)}>Edit</Btn>
                <Btn color="red" size="sm" onClick={() => setConfirm(post)}>Delete</Btn>
              </div>
            </div>
          ))
        )}
      </Card>

      {editor !== null && (
        <PostEditor initial={editor} onSave={handleSave} onClose={() => setEditor(null)} />
      )}
      {confirm && (
        <DeleteModal post={confirm} onConfirm={handleDelete} onClose={() => setConfirm(null)} />
      )}
    </div>
  );
};
